import React from 'react';
import { Button, Card, Input, Popconfirm, TimePicker, Tooltip } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { MAX_LIST_SIZE } from '../constants';
import {
    DeliveryTime,
    DeliveryTimeCategory,
    DeliveryTimeOtherEntry,
    DeliveryTimeRange,
    countDeliveryTimeRanges,
    emptyOtherEntry,
} from '../utils/deliveryTime';

// TimePicker 用 dayjs, dayjs 解析 "HH:mm" 需要 customParseFormat 插件
dayjs.extend(customParseFormat);

const TIME_FORMAT = 'HH:mm';

interface Props {
    value: DeliveryTime;
    onChange: (next: DeliveryTime) => void;
}

/**
 * 配送时间结构化编辑器。
 *
 * <p>4 个分类: 工作日 / 休息日 / 节假日 (固定 3 个) + 其他 (用户自定义 list)。
 * 每个分类 / 其他条目内部都有 ranges 列表 + remark 输入。
 *
 * <p>**上限语义**: {@link MAX_LIST_SIZE} (50) 是**整个配送时间的总时段上限**, 不是每个 category 各自的;
 * 即 workday + restday + holiday + 所有 other 条目的 ranges 加起来不能超过 50。
 * 当总数达上限, 所有 "+ 新增时段" 按钮一起 disabled。
 *
 * <p>时段允许 end < start (跨日), 00:00 - 24:00 表示全天 (前端不做强校验, parse 后端 JSON 时已 normalize)。
 */
const DeliveryTimeEditor: React.FC<Props> = ({ value, onChange }) => {
    const update = (patch: Partial<DeliveryTime>) => onChange({ ...value, ...patch });

    const updateCategory = (key: 'workday' | 'restday' | 'holiday', next: DeliveryTimeCategory) => {
        update({ [key]: next } as Partial<DeliveryTime>);
    };

    /* ====================== 其他 列表 CRUD ====================== */
    const addOther = () => {
        update({ other: [...value.other, emptyOtherEntry()] });
    };
    const removeOther = (idx: number) => {
        update({ other: value.other.filter((_, i) => i !== idx) });
    };
    const updateOther = (idx: number, patch: Partial<DeliveryTimeOtherEntry>) => {
        update({
            other: value.other.map((e, i) => (i === idx ? { ...e, ...patch } : e)),
        });
    };

    // 总时段数 = 3 个固定分类 + 所有 other 条目里 ranges 加起来; 全局共享 50 上限
    const totalRanges = countDeliveryTimeRanges(value);
    const reachedRangeLimit = totalRanges >= MAX_LIST_SIZE;

    return (
        <div className="space-y-3">
            <div className="text-xs text-gray-500">
                总时段数: <span className={reachedRangeLimit ? 'text-red-500 font-medium' : 'text-gray-700 font-medium'}>{totalRanges}</span> / {MAX_LIST_SIZE}
            </div>

            <CategoryBlock
                title="工作日"
                category={value.workday}
                reachedRangeLimit={reachedRangeLimit}
                onChange={(c) => updateCategory('workday', c)}
            />
            <CategoryBlock
                title="休息日"
                category={value.restday}
                reachedRangeLimit={reachedRangeLimit}
                onChange={(c) => updateCategory('restday', c)}
            />
            <CategoryBlock
                title="节假日"
                category={value.holiday}
                reachedRangeLimit={reachedRangeLimit}
                onChange={(c) => updateCategory('holiday', c)}
            />

            {/* 其他 - 用户自定义命名条目列表;
                新增条目本身不受 ranges 上限管 (空条目不占额度), 但条目内部的 + 新增时段 受总上限管 */}
            <Card
                size="small"
                title={<span className="font-medium">其他</span>}
                extra={<AddButton text="新增条目" disabled={false} onClick={addOther} />}
            >
                {value.other.length === 0 ? (
                    <span className="text-xs text-gray-400">如复活节 / 黑五 / 双 11 等自定义时段</span>
                ) : (
                    <div className="space-y-2">
                        {value.other.map((entry, idx) => (
                            <OtherEntryEditor
                                key={idx}
                                entry={entry}
                                reachedRangeLimit={reachedRangeLimit}
                                onChange={(patch) => updateOther(idx, patch)}
                                onRemove={() => removeOther(idx)}
                            />
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
};

/* ====================== 子组件 ====================== */

/** 单个固定分类块 (工作日 / 休息日 / 节假日)。 */
const CategoryBlock: React.FC<{
    title: string;
    category: DeliveryTimeCategory;
    /** 整个配送时间总时段数到上限了 - + 新增时段 一律 disabled */
    reachedRangeLimit: boolean;
    onChange: (next: DeliveryTimeCategory) => void;
}> = ({ title, category, reachedRangeLimit, onChange }) => {
    return (
        <Card
            size="small"
            title={<span className="font-medium">{title}</span>}
            extra={
                <AddButton
                    text="新增时段"
                    disabled={reachedRangeLimit}
                    onClick={() => onChange({ ...category, ranges: [...category.ranges, { start: '09:00', end: '18:00' }] })}
                />
            }
        >
            <RangesEditor
                ranges={category.ranges}
                onChange={(ranges) => onChange({ ...category, ranges })}
            />
            <div className="mt-2">
                <div className="text-xs text-gray-500 mb-1">备注</div>
                <Input
                    placeholder="例如: 不含午休 12:00-13:00"
                    value={category.remark ?? ''}
                    onChange={(e) => onChange({ ...category, remark: e.target.value || null })}
                    maxLength={200}
                />
            </div>
        </Card>
    );
};

/** 其他列表中的单条编辑器。 */
const OtherEntryEditor: React.FC<{
    entry: DeliveryTimeOtherEntry;
    /** 整个配送时间总时段数到上限了 - + 新增时段 一律 disabled */
    reachedRangeLimit: boolean;
    onChange: (patch: Partial<DeliveryTimeOtherEntry>) => void;
    onRemove: () => void;
}> = ({ entry, reachedRangeLimit, onChange, onRemove }) => {
    return (
        <div className="p-2 border rounded space-y-2 bg-gray-50">
            <div className="flex items-center gap-2">
                <Input
                    placeholder="名称 (如: 复活节)"
                    value={entry.name}
                    onChange={(e) => onChange({ name: e.target.value })}
                    maxLength={50}
                    style={{ flex: 1 }}
                />
                <Popconfirm
                    title={`确认删除条目「${entry.name || '(未命名)'}」?`}
                    okText="确定"
                    cancelText="取消"
                    okButtonProps={{ danger: true }}
                    onConfirm={onRemove}
                >
                    <Button
                        size="small"
                        danger
                        type="link"
                        icon={<DeleteOutlined />}
                    >删除</Button>
                </Popconfirm>
            </div>
            <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">时段</span>
                <AddButton
                    text="新增时段"
                    disabled={reachedRangeLimit}
                    onClick={() => onChange({ ranges: [...entry.ranges, { start: '09:00', end: '18:00' }] })}
                />
            </div>
            <RangesEditor
                ranges={entry.ranges}
                onChange={(ranges) => onChange({ ranges })}
            />
            <div>
                <div className="text-xs text-gray-500 mb-1">备注</div>
                <Input
                    placeholder="例如: 03/29-04/01"
                    value={entry.remark ?? ''}
                    onChange={(e) => onChange({ remark: e.target.value || null })}
                    maxLength={200}
                />
            </div>
        </div>
    );
};

/** ranges 列表编辑器 - 每行一个 RangePicker + 删除按钮。 */
const RangesEditor: React.FC<{
    ranges: DeliveryTimeRange[];
    onChange: (next: DeliveryTimeRange[]) => void;
}> = ({ ranges, onChange }) => {
    if (ranges.length === 0) {
        return <div className="text-xs text-gray-400 py-1">无时段</div>;
    }

    const updateRange = (idx: number, patch: Partial<DeliveryTimeRange>) => {
        onChange(ranges.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    };
    const removeRange = (idx: number) => {
        onChange(ranges.filter((_, i) => i !== idx));
    };

    return (
        <div className="space-y-1">
            {ranges.map((r, idx) => (
                <div key={idx} className="flex items-center gap-2">
                    <TimePicker
                        format={TIME_FORMAT}
                        value={parseHHmm(r.start)}
                        onChange={(d) => updateRange(idx, { start: formatHHmm(d) })}
                        allowClear={false}
                        size="small"
                        placeholder="开始"
                        style={{ width: 90 }}
                    />
                    <span className="text-gray-400">~</span>
                    <TimePicker
                        format={TIME_FORMAT}
                        value={parseHHmm(r.end)}
                        onChange={(d) => updateRange(idx, { end: formatHHmm(d) })}
                        allowClear={false}
                        size="small"
                        placeholder="结束"
                        style={{ width: 90 }}
                    />
                    {r.end < r.start && (
                        <Tooltip title="结束早于开始, 视为跨日">
                            <span className="text-xs text-amber-600">跨日</span>
                        </Tooltip>
                    )}
                    <div className="flex-1" />
                    <Button
                        size="small"
                        danger
                        type="link"
                        icon={<DeleteOutlined />}
                        onClick={() => removeRange(idx)}
                    />
                </div>
            ))}
        </div>
    );
};

const AddButton: React.FC<{ text: string; disabled: boolean; onClick: () => void }> = ({ text, disabled, onClick }) => {
    const btn = (
        <Button size="small" icon={<PlusOutlined />} disabled={disabled} onClick={onClick}>{text}</Button>
    );
    if (disabled) {
        return <Tooltip title={`配送时间总时段数已达上限 ${MAX_LIST_SIZE}`}>{btn}</Tooltip>;
    }
    return btn;
};

/* ====================== 时间格式转换 ====================== */

/**
 * "HH:mm" → Dayjs. 24:00 是特殊值, dayjs 不直接支持, 这里转成 "00:00 次日" 不太合适,
 * 实际场景下 TimePicker 编辑时直接显示 23:59 + 当条提示, 简化处理。
 */
function parseHHmm(s: string): Dayjs | null {
    if (!s) return null;
    if (s === '24:00') return dayjs('23:59', TIME_FORMAT); // 显示用, 序列化时仍是 23:59
    return dayjs(s, TIME_FORMAT);
}

function formatHHmm(d: Dayjs | null): string {
    if (!d) return '00:00';
    return d.format(TIME_FORMAT);
}

export default DeliveryTimeEditor;
